import { WebAuthnError } from "@simplewebauthn/browser";

export type PasskeyFailureOutcome = {
  toast: "failed" | "unsupported" | null;
  report: "issue" | "log" | null;
};

type PasskeyAttempt = {
  conditional: boolean;
};

function errorName(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null || !("name" in error)) {
    return undefined;
  }
  return typeof error.name === "string" ? error.name : undefined;
}

function isAutofillArmingGuard(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    (message.includes("webauthn") && message.includes("autocomplete")) ||
    message.includes("does not support webauthn autofill")
  );
}

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }
  const { response } = error;
  if (typeof response !== "object" || response === null) return undefined;
  return "status" in response && typeof response.status === "number"
    ? response.status
    : undefined;
}

export function resolvePasskeyFailure(
  error: unknown,
  { conditional }: PasskeyAttempt,
): PasskeyFailureOutcome {
  if (
    error instanceof WebAuthnError &&
    error.code === "ERROR_CEREMONY_ABORTED"
  ) {
    return { toast: null, report: null };
  }

  // Arming conditional UI throws a plain Error when the browser can't back
  // autofill at guard time — either no <input> carrying a "webauthn"
  // autocomplete token is in the DOM (the form re-rendered into the MFA step
  // or unmounted between arming and the check) or the browser reports no
  // autofill support despite the earlier capability probe. The autofill
  // attempt is silent, so it is never worth a toast or an issue.
  if (conditional && isAutofillArmingGuard(error)) {
    return { toast: null, report: "log" };
  }

  // Dismissed, timed out, denied, an authenticator/credential manager the
  // device cannot reach, or a ceremony that overlapped another still-pending
  // one (OperationError "A request is already pending.", seen on Samsung
  // Internet): the browser's verdict, not a fault the app can fix.
  const name = errorName(error);
  if (
    name === "NotAllowedError" ||
    name === "NotSupportedError" ||
    name === "NotReadableError" ||
    name === "OperationError"
  ) {
    if (conditional) return { toast: null, report: "log" };
    return {
      toast: name === "NotSupportedError" ? "unsupported" : "failed",
      report: "log",
    };
  }

  const status = responseStatus(error);
  if (status !== undefined && status >= 400 && status < 500) {
    return { toast: "failed", report: null };
  }

  return { toast: "failed", report: "issue" };
}
